import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { Navbar } from "@/components/landing/Navbar";
import { FooterSection } from "@/components/landing/FooterSection";
import { MarkdownBody } from "@/components/blog/MarkdownBody";
import { getArticleBySlug } from "@/lib/blog.functions";
import { Reveal } from "@/components/Reveal";
import { Calendar, Clock, User, ShieldCheck, ArrowUpRight, HelpCircle, Sparkles } from "lucide-react";

function readingTime(text?: string | null) {
  if (!text) return 3;
  const words = text.split(/\s+/).length;
  return Math.max(2, Math.round(words / 220));
}

const SITE = "https://heallexa.com";
const DEFAULT_OG_IMAGE = `${SITE}/og-image.jpg`;
const LOGO_URL = `${SITE}/logo.png`;

export const Route = createFileRoute("/blog/article/$slug")({
  loader: async ({ params }) => {
    await setSeoPageCache();
    const res = await getArticleBySlug({ data: { slug: params.slug } });
    if (!res) throw notFound();
    return res;
  },
  head: ({ loaderData }) => {
    const a = loaderData?.article;
    if (!a) return { meta: [] };
    const title = a.meta_title || a.title;
    const desc = a.meta_description || a.excerpt || "Healthcare discovery guide from Heallexa.";
    const canonical = a.canonical_url || `${SITE}/blog/article/${a.slug}`;
    const ogImage = (a as any).og_image || DEFAULT_OG_IMAGE;

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: a.title,
      datePublished: a.published_at,
      dateModified: a.last_updated_at,
      image: ogImage,
      author: {
        "@type": "Organization",
        name: a.author_name || "Heallexa Editorial Team",
      },
      publisher: {
        "@type": "Organization",
        name: "Heallexa",
        url: SITE,
        logo: { "@type": "ImageObject", url: LOGO_URL },
      },
      mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
      url: canonical,
      description: desc,
      isPartOf: { "@id": `${SITE}/blog` },
    };

    const faqSchema =
      Array.isArray(a.faq_json) && a.faq_json.length > 0
        ? {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: (a.faq_json as any[]).map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }
        : null;

    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "article" },
        { property: "og:image", content: ogImage },
        { property: "article:published_time", content: a.published_at ?? "" },
        { property: "article:modified_time", content: a.last_updated_at ?? "" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        { name: "twitter:image", content: ogImage },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(articleSchema) },
        ...(faqSchema
          ? [{ type: "application/ld+json", children: JSON.stringify(faqSchema) }]
          : []),
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-32 mx-auto max-w-3xl px-6 text-center">
        <h1 className="text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">Article not found</h1>
        <Link to="/blog" className="text-primary underline mt-4 inline-block text-[14px]">Back to blog</Link>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-32 mx-auto max-w-3xl px-6">
        <p className="text-destructive">{error.message}</p>
      </div>
    </div>
  ),
  component: BlogArticlePage,
});

function BlogArticlePage() {
  const { article, category } = Route.useLoaderData();
  const a = article as any;
  const internalLinks = (a.internal_links_json ?? []) as Array<{ url: string; label: string }>;
  const faq = (a.faq_json ?? []) as Array<{ q: string; a: string }>;
  const minutes = readingTime(a.content || a.excerpt);
  const publishedDate = a.published_at
    ? new Date(a.published_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })
    : null;
  const isEditorsPick = typeof a.seo_score === "number" && a.seo_score >= 80;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <article className="pt-32 md:pt-28 pb-16 px-6">
        <div className="mx-auto max-w-[720px]">
          {/* Breadcrumb / chips */}
          <div className="flex flex-wrap items-center gap-2 text-[13px]">
            <Link to="/blog" className="text-muted-foreground hover:text-foreground transition-colors">Blog</Link>
            {category && (
              <>
                <span className="text-muted-foreground" aria-hidden="true">/</span>
                <Link
                  to="/blog/$category"
                  params={{ category: category.slug }}
                  className="inline-flex h-6 items-center rounded-sm border border-border bg-background px-2 text-[11px] leading-none border-b-2 border-b-primary text-foreground hover:bg-muted transition-colors"
                >
                  {category.name}
                </Link>
              </>
            )}
            {isEditorsPick && (
              <span className="inline-flex h-6 items-center gap-1 rounded-sm border border-border bg-background px-2 text-[11px] leading-none text-muted-foreground">
                <Sparkles className="w-3 h-3 text-primary" /> Editor's pick
              </span>
            )}
          </div>

          <h1 className="mt-4 text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">
            {a.h1 || a.title}
          </h1>
          {a.excerpt && (
            <p className="mt-4 text-[15px] leading-7 text-muted-foreground">{a.excerpt}</p>
          )}

          {/* Meta strip */}
          <div className="mt-6 flex flex-wrap items-center gap-4 text-[13px] text-muted-foreground">
            {a.author_name && (
              <span className="inline-flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {a.author_name}</span>
            )}
            {a.reviewer_name && (
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-primary" /> Reviewed by {a.reviewer_name}</span>
            )}
            {publishedDate && (
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {publishedDate}</span>
            )}
            <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {minutes} min read</span>
          </div>

          <div className="mt-8 border-t border-border" />

          {/* TL;DR card */}
          {a.excerpt && (
            <div className="card-surface rounded-md p-6 mt-8">
              <p className="text-[11px] font-semibold text-primary tracking-[0.18em] uppercase">In short</p>
              <p className="mt-2 text-[15px] leading-7 text-foreground/80">{a.excerpt}</p>
            </div>
          )}

          <div className="mt-8">
            {a.content ? <MarkdownBody source={a.content} /> : <p className="text-[15px] leading-7 text-muted-foreground">Content coming soon.</p>}
          </div>

          {faq.length > 0 && (
            <Reveal as="section" className="mt-16 border-t border-border pt-10">
              <div className="flex items-center gap-2 mb-6">
                <HelpCircle className="w-4 h-4 text-primary" />
                <h2 className="text-[22px] font-semibold tracking-tight text-foreground">Frequently asked questions</h2>
              </div>
              <div className="space-y-3">
                {faq.map((f, i) => (
                  <div key={i} className="card-surface-flat rounded-md p-5">
                    <div className="flex items-start gap-4">
                      <span className="flex-shrink-0 h-7 w-7 inline-flex items-center justify-center rounded-md border border-primary/30 bg-primary/10 text-primary text-[12px] font-semibold">
                        {i + 1}
                      </span>
                      <div className="flex-1">
                        <p className="text-[15px] font-semibold text-foreground leading-tight">{f.q}</p>
                        <p className="mt-2 text-[13.5px] leading-relaxed text-muted-foreground">{f.a}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {internalLinks.length > 0 && (
            <Reveal as="section" className="mt-16 border-t border-border pt-10">
              <div className="flex items-center gap-2 mb-6">
                <ArrowUpRight className="w-4 h-4 text-primary" />
                <h2 className="text-[22px] font-semibold tracking-tight text-foreground">Explore on Heallexa</h2>
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {internalLinks.map((l, i) => (
                  <li key={i} className="h-full">
                    <a
                      href={l.url}
                      className="group hover-lift card-surface-flat flex h-full items-center justify-between gap-3 rounded-md p-4"
                    >
                      <span className="text-[13.5px] font-medium text-foreground">{l.label}</span>
                      <ArrowUpRight className="w-4 h-4 text-primary transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  </li>
                ))}
              </ul>
            </Reveal>
          )}

          {a.medical_disclaimer && (
            <div className="card-surface-flat mt-16 rounded-md p-5">
              <div className="flex items-start gap-3">
                <ShieldCheck className="w-4 h-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                <p className="text-[12px] leading-relaxed text-muted-foreground">{a.medical_disclaimer}</p>
              </div>
            </div>
          )}
        </div>
      </article>
      <FooterSection />
    </div>
  );
}
